<template>
  <BaseElSelect
    v-model="syncModel"
    v-bind="$attrs"
    filterable
    remote
    clearable
    reserve-keyword
    :placeholder="placeholder || $t('orderInfoBlock.memberSearch.placeholder')"
    :no-match-text="$t('common.select.noMatch.text')"
    :no-data-text="$t('common.table.empty.text')"
    :remote-method="remoteSearch"
    :loading="loading"
    value-key="id"
    @change="onChange"
    @clear="$emit('clear')"
    @visible-change="onVisibleChange"
  >
    <i slot="prefix" class="el-input__icon el-icon-search" />
    <BaseElSelectOption
      v-for="item in membersList"
      :key="item.id"
      :label="item.UserInfo.phone ? `${item.UserInfo.name} (${item.UserInfo.phone})` : item.UserInfo.name"
      :value="item"
    >
      <span class="inline-flex items-center gap-2">
        <span>{{ item.UserInfo.phone ? `${item.UserInfo.name} (${item.UserInfo.phone})` : item.UserInfo.name }}</span>
        <el-tag
          v-if="getMemberStatusLabel(item)"
          size="mini"
          :type="getMemberStatusTagType(item)"
        >
          {{ getMemberStatusLabel(item) }}
        </el-tag>
      </span>
    </BaseElSelectOption>
  </BaseElSelect>
</template>
<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { GetMembers } from '@/api/member'
import { get, find } from 'lodash'
import { useShop } from '@/use/shop'

export default defineComponent({
  name: 'MemberSearch',

  props: {
    value: [Object, Array],
    // 保留 model 以向後相容
    model: [Object, Array],
    type: String,
    placeholder: String,
  },
  emits: ['input', 'update:model', 'change', 'clear', 'visibleChange', 'getMember'],

  setup (props, { emit }) {
    const loading = ref(false)
    const membersList = ref([])

    const { shopId } = useShop()

    // v-model 雙向綁定
    const syncModel = computed({
      get () {
        // 優先使用 value (標準 v-model)，否則使用 model (向後相容)
        return props.value !== undefined ? props.value : props.model
      },
      set (data) {
        // 同時發出兩種事件以保持向後相容
        emit('input', data)
        emit('update:model', data)
      },
    })

    /**
     * 遠端搜尋會員
     * @param {string} query - 搜尋關鍵字
     */
    const remoteSearch = async (query) => {
      query = query.trim()
      if (query === '') membersList.value = []

      try {
        loading.value = true
        setTimeout(async () => {
          await getMember(query)
          loading.value = false
        }, 200)
      } catch (error) {
        console.log(error)
      }
    }

    /**
     * 處理選項變更事件
     * @param {Object} member - 選中的會員物件
     */
    const onChange = (member) => {
      emit('change', member)
    }

    /**
     * 取得會員列表
     * @param {string|null} query - 搜尋關鍵字
     */
    const getMember = async (query = null) => {
      try {
        const res = await GetMembers({
          shopId: shopId.value,
          name: query || undefined,
          phone: query || undefined,
          limit: 50,
          existPhone: false,
        })
        membersList.value = res
        emit('getMember', res, query)
      } catch (error) {
        console.log(error)
      }
    }

    /**
     * 處理下拉選單顯示狀態變更
     * @param {boolean} visible - 是否顯示
     */
    const onVisibleChange = async (visible) => {
      if (visible) {
        await remoteSearch('')
      }
      emit('visibleChange', visible)
    }

    // 初始化：載入當前選中的會員到列表中
    onMounted(async () => {
      await remoteSearch('')
      const currentValue = syncModel.value
      // 處理單選模式：值是單一物件
      if (get(currentValue, 'id') && !find(membersList.value, ['id', currentValue.id])) {
        membersList.value.push(currentValue)
      }
      // 處理多選模式：值是陣列
      if (Array.isArray(currentValue) && currentValue.length > 0) {
        currentValue.forEach(member => {
          if (get(member, 'id') && !find(membersList.value, ['id', member.id])) {
            membersList.value.push(member)
          }
        })
      }
    })

    /**
     * 取得會員狀態標籤文字（僅警告/黑名單顯示）
     * @param {Object} member - 會員物件
     * @returns {string|null}
     */
    const getMemberStatusLabel = (member) => {
      const status = member.memberStatus || (member.isBlock ? 'blocked' : 'normal')
      if (status === 'blocked') return '黑名單'
      if (status === 'warning') return '警告'
      return null
    }

    /**
     * 取得會員狀態 Tag 類型
     * @param {Object} member - 會員物件
     * @returns {string}
     */
    const getMemberStatusTagType = (member) => {
      const status = member.memberStatus || (member.isBlock ? 'blocked' : 'normal')
      if (status === 'blocked') return 'danger'
      if (status === 'warning') return 'warning'
      return 'info'
    }

    return {
      loading,
      membersList,
      syncModel,
      remoteSearch,
      onChange,
      getMember,
      onVisibleChange,
      getMemberStatusLabel,
      getMemberStatusTagType,
    }
  },
})
</script>

<style scoped lang="postcss">
/* ::v-deep .el-icon-::before {
  content: '\e778';
} */

::v-deep.el-select .el-input .el-select__caret {
  @apply transform rotate-0;
}
</style>
